import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { DollarSign, Loader2, Save } from "lucide-react";

interface Booking {
  id: string;
  user_id: string;
  deposit_paid_amount?: number | null;
  price?: number | null;
}

interface Props {
  booking: Booking;
  /** The clickable badge / element that opens the popover */
  trigger: React.ReactNode;
  /** Called after a successful save so the parent can refetch. */
  onSaved?: () => void;
  align?: "start" | "center" | "end";
}

const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "tap_to_pay", label: "Tap to Pay" },
  { value: "zelle", label: "Zelle" },
  { value: "venmo", label: "Venmo" },
  { value: "cashapp", label: "Cash App" },
  { value: "other", label: "Other" },
];

/**
 * Small popover that lets an admin record a deposit amount on a booking.
 * - Updates bookings.deposit_paid_amount
 * - Logs a manual_payments row so it appears in revenue
 * - Optionally bumps payment_status to "paid" if the deposit covers the full price
 */
const ManualDepositPopover = ({ booking, trigger, onSaved, align = "end" }: Props) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<string>(
    booking.deposit_paid_amount && Number(booking.deposit_paid_amount) > 0
      ? String(booking.deposit_paid_amount)
      : ""
  );
  const [method, setMethod] = useState<string>("cash");
  const [notes, setNotes] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast({ title: "Enter an amount", description: "Deposit must be greater than $0.", variant: "destructive" });
      return;
    }
    setSaving(true);

    // 1. Update booking deposit — never auto-flip payment_status to "paid".
    // A deposit is always just a deposit; "Paid in Full" is set only when the
    // admin collects payment via the Collect Payment flow.
    const updateData: any = { deposit_paid_amount: amt, payment_type: method };

    const { error: updErr } = await supabase
      .from("bookings")
      .update(updateData)
      .eq("id", booking.id);

    if (updErr) {
      setSaving(false);
      toast({ title: "Save failed", description: updErr.message, variant: "destructive" });
      return;
    }

    // 2. Log a manual payment so it shows in revenue
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const { error: insErr } = await supabase.from("manual_payments").insert({
      booking_id: booking.id,
      user_id: booking.user_id,
      collected_by: authUser?.id,
      method,
      subtotal: amt,
      tip_amount: 0,
      tax_amount: 0,
      total_amount: amt,
      notes: notes ? `Deposit · ${notes}` : "Deposit (manual entry)",
    });

    setSaving(false);

    if (insErr) {
      // Booking was updated but payment row failed — surface the error so admin can retry
      toast({ title: "Deposit saved, payment log failed", description: insErr.message, variant: "destructive" });
    } else {
      toast({ title: "Deposit recorded", description: `$${amt.toFixed(2)} via ${method}` });
    }

    setOpen(false);
    onSaved?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-72 bg-card border-border/50 p-3 space-y-3"
        align={align}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-primary" />
          <p className="text-xs tracking-[0.2em] uppercase font-medium">Record Deposit</p>
        </div>

        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">
            Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="pl-7 bg-background border-border/50 h-9 text-sm"
              autoFocus
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">
            Method
          </label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="bg-background border-border/50 h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {METHODS.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">
            Notes <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
          </label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Zelle ref #, etc."
            className="bg-background border-border/50 h-9 text-sm"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={saving}
            className="text-[11px] tracking-widest uppercase text-muted-foreground"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !amount}
            className="text-[11px] tracking-widest uppercase gap-1"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ManualDepositPopover;
